/**
 * Shared verification result model — every probe returns VerificationResult.
 * Operational Truth derives status from evidence; nothing is healthy without proof.
 *
 * Evidence quality (UI): Current | Aging | Expired — never expose "fresh"/"stale".
 */

export type VerificationStatus = "verified" | "warning" | "failed" | "unknown";
export type VerificationCategory =
  | "dns"
  | "http"
  | "tls"
  | "application"
  | "database"
  | "integration"
  | "infrastructure"
  | "runtime_doctor"
  | "backup"
  | "queue"
  | "worker"
  | "other";

/** Wire + UI evidence quality. Legacy fresh/stale normalize via normalizeEvidenceQuality. */
export type VerificationFreshness = "current" | "aging" | "expired" | "unknown";
export type VerificationConfidence = "high" | "medium" | "low";

export type VerificationEvidenceItem = {
  label: string;
  value: string;
};

export type VerificationResult = {
  component: string;
  category: VerificationCategory | string;
  status: VerificationStatus;
  verification_method: string;
  timestamp: string;
  duration_ms?: number | null;
  endpoint?: string | null;
  evidence: VerificationEvidenceItem[];
  recommendation?: string | null;
  confidence?: VerificationConfidence | null;
  next_check?: string | null;
  freshness?: VerificationFreshness | null;
  raw?: Record<string, unknown>;
  /** Correlation identifiers — link a verification to the event that triggered it. */
  deployment_id?: string | null;
  incident_id?: string | null;
  request_id?: string | null;
  trace_id?: string | null;
};

export type EvidenceQualityThresholds = {
  /** Age below this → Evidence: Current */
  currentMaxSeconds: number;
  /** Age at/above this → Evidence: Expired; between → Aging */
  expiredAfterSeconds: number;
};

const DEFAULT_THRESHOLDS: EvidenceQualityThresholds = {
  currentMaxSeconds: 300,
  expiredAfterSeconds: 1800,
};

/** Configurable freshness thresholds per verification type (category). */
export const EVIDENCE_QUALITY_THRESHOLDS: Record<string, EvidenceQualityThresholds> = {
  http: { currentMaxSeconds: 300, expiredAfterSeconds: 900 },
  dns: { currentMaxSeconds: 600, expiredAfterSeconds: 3600 },
  tls: { currentMaxSeconds: 600, expiredAfterSeconds: 3600 },
  application: { currentMaxSeconds: 300, expiredAfterSeconds: 900 },
  database: { currentMaxSeconds: 120, expiredAfterSeconds: 600 },
  integration: { currentMaxSeconds: 600, expiredAfterSeconds: 3600 },
  infrastructure: { currentMaxSeconds: 300, expiredAfterSeconds: 1800 },
  runtime_doctor: { currentMaxSeconds: 300, expiredAfterSeconds: 1800 },
  backup: { currentMaxSeconds: 3600, expiredAfterSeconds: 86400 },
  queue: { currentMaxSeconds: 300, expiredAfterSeconds: 900 },
  worker: { currentMaxSeconds: 300, expiredAfterSeconds: 900 },
  other: { ...DEFAULT_THRESHOLDS },
};

/** @deprecated use EVIDENCE_QUALITY_THRESHOLDS — kept for import compat */
export const FRESH_SECONDS = DEFAULT_THRESHOLDS.currentMaxSeconds;
/** @deprecated use EVIDENCE_QUALITY_THRESHOLDS */
export const CURRENT_SECONDS = DEFAULT_THRESHOLDS.expiredAfterSeconds;

export function evidenceThresholds(
  category?: string | null,
): EvidenceQualityThresholds {
  const key = (category || "other").trim().toLowerCase() || "other";
  const configured = EVIDENCE_QUALITY_THRESHOLDS[key] ?? DEFAULT_THRESHOLDS;
  const currentMaxSeconds = configured.currentMaxSeconds;
  let expiredAfterSeconds = configured.expiredAfterSeconds;
  if (expiredAfterSeconds <= currentMaxSeconds) {
    expiredAfterSeconds = currentMaxSeconds + 1;
  }
  return { currentMaxSeconds, expiredAfterSeconds };
}

export function parseVerificationTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Normalize legacy wire values (fresh/stale) and synonyms. */
export function normalizeEvidenceQuality(
  raw: string | null | undefined,
): VerificationFreshness {
  if (!raw) return "unknown";
  const value = raw.trim().toLowerCase();
  if (value === "fresh" || value === "current") return "current";
  if (value === "aging") return "aging";
  if (value === "stale" || value === "expired") return "expired";
  if (value === "unknown") return "unknown";
  return "unknown";
}

export function computeFreshness(
  timestamp: string | null | undefined,
  now: Date = new Date(),
  category?: string | null,
): VerificationFreshness {
  const observed = parseVerificationTimestamp(timestamp);
  if (!observed) return "unknown";
  const ageSec = (now.getTime() - observed.getTime()) / 1000;
  const { currentMaxSeconds, expiredAfterSeconds } = evidenceThresholds(category);
  if (ageSec < 0) return "current";
  if (ageSec < currentMaxSeconds) return "current";
  if (ageSec < expiredAfterSeconds) return "aging";
  return "expired";
}

/** Operator-facing evidence quality label — never "Fresh" / "Stale". */
export function freshnessLabel(freshness: VerificationFreshness | string | null | undefined): string {
  switch (normalizeEvidenceQuality(freshness ?? null)) {
    case "current":
      return "Evidence: Current";
    case "aging":
      return "Evidence: Aging";
    case "expired":
      return "Evidence: Expired";
    default:
      return "Evidence: Unknown";
  }
}

/** Same as freshnessLabel with status emoji for domain/integration cards. */
export function evidenceQualityBadge(
  freshness: VerificationFreshness | string | null | undefined,
): string {
  switch (normalizeEvidenceQuality(freshness ?? null)) {
    case "current":
      return "🟢 Evidence: Current";
    case "aging":
      return "🟡 Evidence: Aging";
    case "expired":
      return "🔴 Evidence: Expired";
    default:
      return "Evidence: Unknown";
  }
}

/** @deprecated alias — use freshnessLabel / evidenceQualityBadge */
export const evidenceQualityLabel = freshnessLabel;

export function formatVerificationAge(
  timestamp: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const observed = parseVerificationTimestamp(timestamp);
  if (!observed) return null;
  const ageSec = Math.max(0, Math.floor((now.getTime() - observed.getTime()) / 1000));
  if (ageSec < 60) return `${ageSec} second${ageSec === 1 ? "" : "s"} ago`;
  const ageMin = Math.floor(ageSec / 60);
  if (ageMin < 60) return `${ageMin} minute${ageMin === 1 ? "" : "s"} ago`;
  const ageHr = Math.floor(ageMin / 60);
  if (ageHr < 24) return `${ageHr} hour${ageHr === 1 ? "" : "s"} ago`;
  const ageDay = Math.floor(ageHr / 24);
  return `${ageDay} day${ageDay === 1 ? "" : "s"} ago`;
}

export function verificationStatusLabel(status: VerificationStatus): string {
  switch (status) {
    case "verified":
      return "Verified";
    case "warning":
      return "Warning";
    case "failed":
      return "Failed";
    default:
      return "Unknown";
  }
}

export function verificationDisplayLabel(
  result: Pick<VerificationResult, "status" | "timestamp">,
  now: Date = new Date(),
): string {
  if (result.status !== "verified" && result.status !== "warning") {
    return verificationStatusLabel(result.status);
  }
  const age = formatVerificationAge(result.timestamp, now);
  return age ? `Verified ${age}` : "Verified";
}

export function aggregateVerificationStatus(
  results: VerificationResult[],
): VerificationStatus {
  const order: Record<VerificationStatus, number> = {
    failed: 4,
    warning: 3,
    unknown: 2,
    verified: 1,
  };
  let worst: VerificationStatus = "verified";
  for (const result of results) {
    if ((order[result.status] ?? 0) > (order[worst] ?? 0)) {
      worst = result.status;
    }
  }
  return results.length ? worst : "unknown";
}

export function latestVerificationTimestamp(
  results: VerificationResult[],
): string | null {
  let latest: string | null = null;
  for (const result of results) {
    if (!result.timestamp) continue;
    if (!latest || new Date(result.timestamp).getTime() > new Date(latest).getTime()) {
      latest = result.timestamp;
    }
  }
  return latest;
}

export function formatEvidenceLines(result: VerificationResult): string {
  return result.evidence.map((e) => `${e.label}: ${e.value}`).join(" · ");
}

export function normalizeVerificationResults(
  input: VerificationResult[] | Record<string, unknown>[] | null | undefined,
): VerificationResult[] {
  if (!input?.length) return [];
  return input
    .filter((row): row is Record<string, unknown> => Boolean(row && typeof row === "object"))
    .map((row) => {
      const timestamp = String(row.timestamp || "");
      const category = (row.category as VerificationCategory) || "other";
      const stored = row.freshness != null ? normalizeEvidenceQuality(String(row.freshness)) : null;
      return {
        component: String(row.component || ""),
        category,
        status: (row.status as VerificationStatus) || "unknown",
        verification_method: String(row.verification_method || ""),
        timestamp,
        duration_ms: (row.duration_ms as number | null | undefined) ?? null,
        endpoint: (row.endpoint as string | null | undefined) ?? null,
        evidence: Array.isArray(row.evidence)
          ? row.evidence
              .filter((e) => e && typeof e === "object")
              .map((e) => ({
                label: String((e as VerificationEvidenceItem).label || ""),
                value: String((e as VerificationEvidenceItem).value || ""),
              }))
              .filter((e) => e.label && e.value)
          : [],
        recommendation: (row.recommendation as string | null | undefined) ?? null,
        confidence: (row.confidence as VerificationConfidence) ?? null,
        next_check: (row.next_check as string | null | undefined) ?? null,
        freshness: timestamp
          ? computeFreshness(timestamp, new Date(), category)
          : stored ?? "unknown",
        raw: (row.raw as Record<string, unknown>) ?? {},
        deployment_id: (row.deployment_id as string | null) ?? null,
        incident_id: (row.incident_id as string | null) ?? null,
        request_id: (row.request_id as string | null) ?? null,
        trace_id: (row.trace_id as string | null) ?? null,
      };
    });
}

export type VerificationTimelineEntry = {
  id: string;
  title: string;
  detail: string;
  observedAt: string;
  status: VerificationStatus;
  component: string;
  category: string;
  verificationMethod: string;
  recommendation: string | null;
};

export function buildVerificationTimeline(
  results: VerificationResult[],
): VerificationTimelineEntry[] {
  return normalizeVerificationResults(results)
    .slice()
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    )
    .map((result, index) => ({
      id: `${result.component}-${result.timestamp}-${index}`,
      title: `${verificationStatusLabel(result.status)} — ${result.component}`,
      detail: formatEvidenceLines(result) || result.verification_method,
      observedAt: result.timestamp,
      status: result.status,
      component: result.component,
      category: String(result.category),
      verificationMethod: result.verification_method,
      recommendation: result.recommendation ?? null,
    }));
}
